import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { customEncode, encrypt } from '@piqet-angular/data';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';

@Component({
  selector: 'piqet-angular-navigation-button',
  templateUrl: './navigation-button.component.html',
  styleUrls: ['./navigation-button.component.scss'],
})
export class NavigationButtonComponent implements OnInit, OnChanges {
  @Input() config: any = {
    scenarioId: 0,
    packageScenarioId: 0,
    eId: 0,
    activatedRouteProjectId: '',
    activatedRoute: null,
    isProjectLocked: false,
    isProductPage: true,
  };

  @Output() beforeNavigation = new EventEmitter<string>(); // Emits 'back' or 'next' before navigation

  menuDataForRouting: any[] = [];
  nextIndex!: number;
  prevIndex!: number;
  nextRoute!: string;
  prevRoute!: string;
  isLastRecord: boolean = false;
  isFirstRecord: boolean = false;

  constructor(
    private router: Router,
    private sharedService: SharedService,
    private swalService: SwalService
  ) {}

  ngOnInit() {
    this.sharedService.menuData$.subscribe((res: any) => {
      this.menuDataForRouting = res;
      this.checkNavigationState();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Check if activatedRoute changed
    if (this.config?.activatedRoute && this.menuDataForRouting?.length > 0) {
      this.checkNavigationState();
    }
  }

  // Combined check for first/last item status
  checkNavigationState() {
    if (
      this.menuDataForRouting &&
      this.menuDataForRouting.length > 0 &&
      this.config?.activatedRoute
    ) {
      const activeIndex = this.menuDataForRouting.findIndex(
        x => x.link === this.config?.activatedRoute
      );
      this.isLastRecord = activeIndex === this.menuDataForRouting.length - 1;
      this.isFirstRecord = activeIndex === 0;

      // Pre-calculate next and previous routes for better performance
      this.findNextRoute(activeIndex);
      this.findPrevRoute(activeIndex);
    }
  }

  backRoute() {
    this.beforeNavigation.emit('back');

    // Use custom back route if provided
    const navigateToRoute = this.prevRoute || 'scenario-configuration';

    // Handle scenario ID appropriately for the previous route as well
    let currentScenarioId = this.config.scenarioId;
    if (
      navigateToRoute == 'fill-data-list' ||
      navigateToRoute == 'wholesale-data-list' ||
      navigateToRoute == 'retail-list' ||
      navigateToRoute == 'consumer-list'
    ) {
      currentScenarioId = this.config.packageScenarioId;
    }

    const valueToEncrypt = `{ "id": ${currentScenarioId}, "packageScenarioId": ${this.config.packageScenarioId}, "eId": ${this.config.eId}}`;
    const encryptedValue = encrypt(valueToEncrypt);
    const encodedValue = encodeURIComponent(this.config.activatedRouteProjectId);

    this.router.navigate(
      [`projects/projects-setting/${customEncode(btoa(encodedValue))}/${navigateToRoute}`],
      {
        queryParams: { param: encryptedValue },
      }
    );
  }

  change() {
    this.beforeNavigation.emit('next');
    if (this.isLastRecord) {
      this.nextPage();
      return;
    }

    const navigateToRoute = this.nextRoute;

    let currentScenarioId = this.config.scenarioId;
    // Handle special cases where scenarioId should be packageScenarioId
    if (
      navigateToRoute == 'fill-data-list' ||
      navigateToRoute == 'wholesale-data-list' ||
      navigateToRoute == 'retail-list' ||
      navigateToRoute == 'consumer-list'
    ) {
      currentScenarioId = this.config.packageScenarioId;
    }

    const valueToEncrypt = `{ "id": ${currentScenarioId}, "packageScenarioId": ${this.config.packageScenarioId}, "eId": ${this.config.eId}}`;
    const encryptedValue = encrypt(valueToEncrypt);
    const encodedValue = encodeURIComponent(this.config.activatedRouteProjectId);

    this.router.navigate(
      [`projects/projects-setting/${customEncode(btoa(encodedValue))}/${navigateToRoute}`],
      {
        queryParams: { param: encryptedValue },
      }
    );
  }

  nextPage() {
    this.swalService
      .confirm('Scenario confirmation', 'Do you want to add another package scenario?')
      .then(async confirmed => {
        if (confirmed) {
          const encodedValue = encodeURIComponent(this.config.activatedRouteProjectId);
          this.sharedService.navigateSiderbarMenu('isScenario'); // For routing directly to scenarios page
          this.router.navigate([
            `projects/projects-setting/${customEncode(btoa(encodedValue))}/scenario-list`,
          ]);
        } else {
          const encodedValue = encodeURIComponent(this.config.activatedRouteProjectId);
          const isCheckReportNavigation = 'isCheckReportNavigation';
          this.sharedService.navigateSiderbarMenu(isCheckReportNavigation);
          try {
            const res = await this.sharedService.checkValidationForProductSector(
              this.config.activatedRouteProjectId
            );
            if (res.data.msg !== '' && res.data.hasOwnProperty('msg')) {
              this.swalService
                .confirm('Report validation', res.data.msg, '', undefined, true)
                .then(confirmed => {
                  if (confirmed) {
                  } else {
                  }
                });
            } else {
              this.router.navigate(
                [`projects/projects-setting/${customEncode(btoa(encodedValue))}/chart-at-glance`],
                {
                  queryParams: { param: customEncode(btoa(encodedValue)) },
                }
              );
            }
          } catch (error) {
            console.error('Error validating product sector:', error);
          }
        }
      });
  }

  findNextRoute(activeIndex?: number) {
    const index =
      activeIndex !== undefined
        ? activeIndex
        : this.menuDataForRouting.findIndex((x: any) => x.link === this.config.activatedRoute);

    this.nextIndex = index + 1;
    if (this.nextIndex < this.menuDataForRouting.length) {
      this.nextRoute = this.menuDataForRouting[this.nextIndex].link;
    }
  }

  findPrevRoute(activeIndex?: number) {
    const index =
      activeIndex !== undefined
        ? activeIndex
        : this.menuDataForRouting.findIndex((x: any) => x.link === this.config.activatedRoute);

    this.prevIndex = index - 1;
    if (this.prevIndex >= 0) {
      this.prevRoute = this.menuDataForRouting[this.prevIndex].link;
    } else {
      // Default to scenario-configuration if we're at the first item
      this.prevRoute = 'scenario-configuration';
    }
  }
}
